import type { ApplicationBorrowerDto } from '@/api/backend/users/applications';
import type { BrokerScenarioBorrowerDto } from '@/api/backend/brokers/scenarios/broker-retrieve-scenario';
import { BorrowerType, TrusteeType } from '@/helpers/const';
import type { BorrowerTypeEnum, TrustStructureEnum, TrusteeTypeEnum } from '@/helpers/const';
import { BorrowerRole } from '@/types/BorrowerGroup';
import type { Address } from '@/types/Address';
import type {
  BorrowerApplicant,
  BorrowerGroup,
  BorrowerPartner,
  BorrowerRoleEnum,
  CompanyDetails,
} from '@/types/BorrowerGroup';

const toRole = (role?: string | null): BorrowerRoleEnum => {
  switch (role) {
    case BorrowerRole.DIRECTOR:
    case BorrowerRole.TRUSTEE:
    case BorrowerRole.PARTNER:
      return role;
    default:
      return BorrowerRole.INDIVIDUAL;
  }
};

/**
 * Backend borrower types, which are finer-grained than the frontend's. The backend never sends a
 * plain 'trust': it distinguishes the trustee structure in the type itself. The frontend model
 * normalises all of them to `trust` and carries the trustee structure in `trustDetails`, which is
 * the same split `mapToApi/borrower-groups.ts` expands back out on submit.
 */
const BE_BORROWER_TYPE = {
  COMPANY: 'company',
  COMPANY_TRUST: 'company_trust',
  CORPORATE_TRUST: 'corporate_trust',
  INDIVIDUAL_TRUST: 'individual_trust',
  INDIVIDUAL_AND_COMPANY_TRUST: 'individual_and_company_trust',
} as const;

/**
 * Normalises a backend borrower type to the frontend type plus, for trusts, the trustee structure.
 * An unrecognised or missing value falls back to INDIVIDUAL (excluded from entity grouping) rather
 * than passing through as a fake company/trust entity.
 */
const toBorrowerType = (type?: string | null): { type: BorrowerTypeEnum; trusteeType?: TrusteeTypeEnum } => {
  switch (type) {
    case BE_BORROWER_TYPE.COMPANY:
      return { type: BorrowerType.COMPANY };
    case BE_BORROWER_TYPE.INDIVIDUAL_TRUST:
      return { type: BorrowerType.TRUST, trusteeType: TrusteeType.INDIVIDUAL };
    case BE_BORROWER_TYPE.COMPANY_TRUST:
    case BE_BORROWER_TYPE.CORPORATE_TRUST:
    case BE_BORROWER_TYPE.INDIVIDUAL_AND_COMPANY_TRUST:
      return { type: BorrowerType.TRUST, trusteeType: TrusteeType.COMPANY };
    default:
      return { type: BorrowerType.INDIVIDUAL };
  }
};

type BorrowerApplicantDto = NonNullable<ApplicationBorrowerDto['borrowerApplicants']>[number];

const partnerFromDto = (ba: BorrowerApplicantDto): BorrowerPartner | undefined => {
  const partner = ba.applicant?.marriedOrDefactoTo;
  if (!partner?.id) return undefined;
  return {
    id: partner.id,
    firstName: partner.firstName ?? undefined,
    lastName: partner.lastName ?? undefined,
    preferredName: partner.preferredName ?? undefined,
  };
};

const companyDetailsFromDto = (dto: ApplicationBorrowerDto): CompanyDetails => ({
  companyName: dto.companyName ?? undefined,
  abnAcnNumber: dto.abnAcn ?? undefined,
  hasRegisteredABN: dto.hasRegisteredAbn ?? undefined,
  isTrading: dto.activelyTrading ?? undefined,
});

export const borrowerGroup = (dto: ApplicationBorrowerDto): BorrowerGroup => {
  const { type, trusteeType } = toBorrowerType(dto.borrowerType);
  const applicants: BorrowerApplicant[] = (dto.borrowerApplicants ?? [])
    .filter((ba) => ba.applicant?.id)
    .map((ba) => ({
      id: ba.id,
      applicantId: ba.applicant?.id ?? '',
      role: toRole(ba.role),
      marriedOrDefactoTo: partnerFromDto(ba),
    }));

  const group: BorrowerGroup = { id: dto.id ?? '', type, applicants };

  if (type === BorrowerType.COMPANY) {
    group.companyDetails = companyDetailsFromDto(dto);
  } else if (type === BorrowerType.TRUST) {
    // The BE stores a single flat borrower row holding both the trust and, for a company-trustee
    // trust, the trustee company's fields. The trustee structure comes from the borrower type
    // itself, not from guessing at whether companyName happens to be populated.
    group.trustDetails = {
      trustName: dto.trustName ?? undefined,
      trustStructure: (dto.trustStructure as TrustStructureEnum | null) ?? undefined,
      trusteeType,
      companyDetails: trusteeType === TrusteeType.COMPANY ? companyDetailsFromDto(dto) : undefined,
    };
  }

  return group;
};

export const borrowerGroups = (dtos?: ApplicationBorrowerDto[] | null): BorrowerGroup[] =>
  (dtos ?? []).map(borrowerGroup);

type ScenarioApplicantRef = { id?: string; applicantType?: string | null };
type ScenarioPersonalOwnershipRef = {
  applicantId?: string | null;
  borrowerId?: string | null;
  incomingPropertyId?: string | null;
  outgoingPropertyId?: string | null;
  supportingPropertyId?: string | null;
  share?: number | null;
};

const SCENARIO_BORROWER_TYPES: (string | undefined)[] = [
  BE_BORROWER_TYPE.COMPANY,
  BE_BORROWER_TYPE.CORPORATE_TRUST,
  BE_BORROWER_TYPE.INDIVIDUAL_TRUST,
];

const scenarioAddress = (dto: BrokerScenarioBorrowerDto): Address | undefined =>
  dto.addressLine1
    ? {
        addressLine: dto.addressLine1,
        addressLine2: dto.addressLine2 ?? undefined,
        locality: dto.addressCity ?? '',
        state: dto.addressState?.toUpperCase() ?? '',
        postcode: dto.addressPostCode ?? '',
        countryCode: 'AU',
        addressManuallyCreated: true,
      }
    : undefined;

const scenarioBorrowerGroup = (dto: BrokerScenarioBorrowerDto, applicantIdMap: Map<string, string>): BorrowerGroup => {
  const role = dto.borrowerType === BE_BORROWER_TYPE.INDIVIDUAL_TRUST ? BorrowerRole.TRUSTEE : BorrowerRole.DIRECTOR;
  const group = borrowerGroup({
    ...dto,
    borrowerApplicants: dto.guarantorApplicantIds.flatMap((guarantorId) => {
      const applicantId = applicantIdMap.get(guarantorId);
      return applicantId ? [{ id: `${applicantId}-${role}`, role, applicant: { id: applicantId } }] : [];
    }),
  } as unknown as ApplicationBorrowerDto);

  const registeredAddress = scenarioAddress(dto);
  if (registeredAddress && group.companyDetails) group.companyDetails.registeredAddress = registeredAddress;
  if (registeredAddress && group.trustDetails?.companyDetails) {
    group.trustDetails.companyDetails.registeredAddress = registeredAddress;
  }
  return group;
};

export type ScenarioBorrowerPrefill = {
  groups: BorrowerGroup[];
  /** The pre-filled borrower group id for each scenario company or trust borrower id. */
  groupIdByBorrowerId: Map<string, string>;
};

/**
 * Pre-fills borrower groups from a scenario's company and trust borrowers. Guarantors are remapped to the
 * `applicant-<index>` ids the scenario applicants are given, and every applicant who guarantees no borrower, or who
 * personally owns a scenario property, becomes an individual borrower. Rows of any other type have no role contract,
 * so they are left out. The primary applicant's borrower comes first.
 */
export const scenarioBorrowerPrefill = (
  borrowers: BrokerScenarioBorrowerDto[],
  applicants: ScenarioApplicantRef[],
  propertiesOwnership: ScenarioPersonalOwnershipRef[] = [],
): ScenarioBorrowerPrefill => {
  const applicantIdMap = new Map(
    applicants.flatMap((applicant, index) => (applicant.id ? [[applicant.id, `applicant-${index}`] as const] : [])),
  );
  const personalOwnerIds = new Set<string | undefined>(
    propertiesOwnership.flatMap((row) => {
      const ownsProperty = row.incomingPropertyId || row.outgoingPropertyId || row.supportingPropertyId;
      const applicantId = !row.borrowerId && row.share && ownsProperty && row.applicantId;
      return applicantId ? [applicantIdMap.get(applicantId)] : [];
    }),
  );
  const entityEntries = borrowers
    .filter((dto) => SCENARIO_BORROWER_TYPES.includes(dto.borrowerType))
    .map((dto) => ({ borrowerId: dto.id, group: scenarioBorrowerGroup(dto, applicantIdMap) }));
  const guarantorIds = new Set(
    entityEntries.flatMap(({ group }) => (group.applicants ?? []).map((a) => a.applicantId)),
  );
  const individualEntries = applicants.flatMap((applicant, index) => {
    const applicantId = `applicant-${index}`;
    if (guarantorIds.has(applicantId) && !personalOwnerIds.has(applicantId)) return [];
    const role = BorrowerRole.INDIVIDUAL;
    const group: BorrowerGroup = {
      id: '',
      type: BorrowerType.INDIVIDUAL,
      applicants: [{ id: `${applicantId}-${role}`, applicantId, role }],
    };
    return [{ borrowerId: undefined, group }];
  });

  const primaryApplicantId = `applicant-${Math.max(
    applicants.findIndex((applicant) => applicant.applicantType === 'primary'),
    0,
  )}`;
  const entries = [...individualEntries, ...entityEntries];
  const primaryIndex = entries.findIndex(({ group }) =>
    group.applicants?.some((a) => a.applicantId === primaryApplicantId),
  );
  if (primaryIndex > 0) entries.unshift(...entries.splice(primaryIndex, 1));
  entries[0]?.group.applicants?.sort(
    (a, b) => Number(b.applicantId === primaryApplicantId) - Number(a.applicantId === primaryApplicantId),
  );

  const groupIdByBorrowerId = new Map<string, string>();
  const groups = entries.map(({ borrowerId, group }, index) => {
    const id = `borrower-${index}`;
    if (borrowerId) groupIdByBorrowerId.set(borrowerId, id);
    return { ...group, id };
  });
  return { groups, groupIdByBorrowerId };
};

export const scenarioBorrowerGroups = (
  borrowers: BrokerScenarioBorrowerDto[],
  applicants: ScenarioApplicantRef[],
): BorrowerGroup[] => scenarioBorrowerPrefill(borrowers, applicants).groups;
